import RemoteApi, { Pagination } from './BaseRemoteApi'
import {
  APIDetail,
  APIPolicy,
  APIPolicyConfiguration,
  AssetResponse,
  ExchangeTemplatePolicy,
  TemplatePolicy
} from './models'
import { ManagedAPI } from '@mulesoft/monitoring-center-components/lib/models/api-manager'

export type APIPolicyBasicBody = {
  apiId: number
  configurationData: APIPolicyConfiguration
  id: number | null
  pointcutData: string | null
  policyTemplateId?: string
  policyVersion?: string
  apiVersionId?: number
}

export type APIPolicyForMule4BasicBody = APIPolicyBasicBody & {
  assetId: string
  assetVersion: string
  groupId: string
}

export type APIPolicyBody =
  | APIPolicyBasicBody
  | APIPolicyForMule4BasicBody

export default class APIManagerRemoteApi extends RemoteApi {

  private static readonly DEFAULT_PAGINATION: Pagination = {limit: 100, offset: 0}

  constructor(authorization: string) {
    super('/apimanager', authorization)
  }

  fetchAPIs(orgId: string, envId: string, pagination?: Pagination): Promise<AssetResponse> {
    const resolvedPagination = pagination ? pagination : APIManagerRemoteApi.DEFAULT_PAGINATION
    const pathElements = ['api', 'v1', 'organizations', orgId, 'environments', envId, 'apis']

    return this._get(pathElements, true, undefined, resolvedPagination)
  }

  fetchManagedAPI(orgId: string, envId: string): Promise<{managedServiceApis: ManagedAPI[]}> {
    const pathElements = ['xapi', 'v1', 'organizations', orgId, 'environments', envId, 'managedServiceApis']

    return this._get(pathElements, true, undefined)
  }

  fetchAPI(orgId: string, envId: string, apiId: string): Promise<APIDetail> {
    return this._get(['api', 'v1', 'organizations', orgId, 'environments', envId, 'apis', apiId.toString()], true)
  }

  fetchPolicyTemplates(orgId: string): Promise<TemplatePolicy[]> {
    return this._get(['api', 'v1', 'organizations', orgId, 'policy-templates'], true)
  }

  fetchExchangePolicyTemplates(orgId: string): Promise<ExchangeTemplatePolicy[]> {
    return this._get(['xapi', 'v1', 'organizations', orgId, 'exchange-policy-templates'], true)
  }

  fetchAPIPolicies(orgId: string, envId: string, apiId: string): Promise<Array<APIPolicy>> {
    const pathElements = ['api', 'v1', 'organizations', orgId, 'environments', envId, 'apis', apiId, 'policies']
    const queryParameters = { fullInfo: false }
    return this._get(pathElements, true, undefined, queryParameters)
  }

  createAPIPolicy(orgId: string, envId: string, apiId: string, body: APIPolicyBody): Promise<APIPolicy> {
    const pathElements = ['api', 'v1', 'organizations', orgId, 'environments', envId, 'apis', apiId, 'policies']
    return this._post(pathElements, body, true)
  }

  updateAPIPolicy(orgId: string, envId: string, apiId: string, policyId: string, body: APIPolicyBody): Promise<APIPolicy> {
    const pathElements = ['api', 'v1', 'organizations', orgId, 'environments', envId, 'apis', apiId, 'policies', policyId]
    return this._patch(pathElements, body, true)
  }

  deleteAPIPolicy(orgId: string, envId: string, apiId: string, policyId: string): Promise<void> {
    const pathElements = ['api', 'v1', 'organizations', orgId, 'environments', envId, 'apis', apiId, 'policies', policyId]
    return this._delete(pathElements, undefined, false)
  }

  setAuthorization(platformToken: string) {
    this.authorization = platformToken
  }

}
